import {
  GraphNode,
  podNodeId,
  serviceNodeId,
} from 'components/InfraMap/graph/graphModel';
import { CliCommandSpec, CliTarget } from 'types/cli';
import { ClusterService } from 'types/infraMap';

export interface CliQuickCommand {
  label: string;
  line: string;
  category: string;
  command: string;
  run: boolean;
}

export interface CliMapTarget {
  targetId: string | null;
  where: string;
  commands: CliQuickCommand[];
}

type ServiceShape = Pick<ClusterService, 'workload' | 'roles'>;

const quick = (
  label: string,
  line: string,
  category: string,
  command: string,
  run = true,
): CliQuickCommand => ({ label, line, category, command, run });

const POD_COMMANDS = [
  quick('Processes', 'top', 'exec', 'top'),
  quick('Memory', 'free', 'exec', 'free'),
  quick('Disk space', 'df -h', 'exec', 'df'),
  quick('Java processes', 'jps', 'exec', 'jps'),
  quick('Follow a log', 'tail -f FILE', 'logs', 'tail', false),
];

const INCORTA_RESOURCES = quick(
  'What Incorta is using',
  'incorta-used-resources',
  'exec',
  'incorta-used-resources',
);

const DISK_SPACE = quick('Disk space', 'df -h', 'exec', 'df');

const serviceGroupCommands = (group: string) => [
  quick('Service state', `${group} service-state`, group, 'service-state'),
  quick(
    'Stream its log',
    `${group} tail-service-logs`,
    group,
    'tail-service-logs',
  ),
];

const HISTORY_SERVER_COMMANDS = [
  ...serviceGroupCommands('sparkhistoryserver'),
  quick(
    'Event logs',
    'sparkhistoryserver list-eventlogs',
    'sparkhistoryserver',
    'list-eventlogs',
  ),
];

const SPARK_COMMANDS = [
  quick('Kyuubi services', 'kyuubi list-services', 'kyuubi', 'list-services'),
  quick('Kyuubi overrides', 'kyuubi list-confs', 'kyuubi', 'list-confs'),
];

const BUCKET_COMMANDS = [
  quick('Browse the bucket', 'tenant ls /', 'tenant', 'ls'),
  quick('Archived logs', 'archived-logs ls /', 'archived-logs', 'ls'),
  quick('Find a file', 'tenant find / --name TEXT', 'tenant', 'find', false),
];

const CLUSTER_COMMANDS = [
  quick('Check the session works', 'exec echo hello', 'exec', 'echo'),
  quick('What you ran here', 'history', 'history', 'list'),
];

// cli-server finds these pods by label, so the CLI names them without a number.
const UNNUMBERED: Record<string, string> = {
  osaka: 'osaka',
  hikari: 'hikari',
  'zaghlul-deployment': 'zaghlul',
  'data-studio-ng': 'data-studio-ng',
};

const NOT_INCORTA = ['cmc', 'copilot'];

const ordinalOf = (workload: string, podName: string) => {
  const rest = podName.startsWith(`${workload}-`)
    ? podName.slice(workload.length + 1)
    : '';
  return /^\d+$/.test(rest) ? Number(rest) : null;
};

const nodeTargetId = (
  cluster: string,
  { workload, roles = [] }: ServiceShape,
  ordinal: number,
) => {
  if (workload === cluster) return `analytics-${ordinal}`;
  if (workload === `${cluster}-loader`) return `loader-${ordinal}`;
  if (workload === `${cluster}-cmc`) return 'cmc';
  if (workload === `${cluster}-copilot`) return 'copilot';
  if (roles.includes('subcluster') && workload.startsWith(`${cluster}-`))
    return `self-managed:${workload.slice(cluster.length + 1)}-${ordinal}`;
  return null;
};

export const cliTargetLabel = (targetId: string) =>
  targetId.replace(/^self-managed:/, '');

function workloadTarget(
  cluster: string,
  service: ServiceShape,
  podName: string | null,
): CliMapTarget | null {
  const { workload, roles = [] } = service;
  const group = UNNUMBERED[workload];
  if (group)
    return {
      targetId: group,
      where: group,
      commands: [...serviceGroupCommands(group), ...POD_COMMANDS.slice(0, 2)],
    };
  if (roles.includes('history') || workload === `${cluster}-history-server`)
    return {
      targetId: null,
      where: 'the Spark history server',
      commands: HISTORY_SERVER_COMMANDS,
    };
  if (roles.includes('spark'))
    return { targetId: null, where: 'Spark', commands: SPARK_COMMANDS };

  const ordinal = podName === null ? 0 : ordinalOf(workload, podName);
  if (ordinal === null) return null;
  const targetId = nodeTargetId(cluster, service, ordinal);
  if (!targetId) return null;
  return {
    targetId,
    where: cliTargetLabel(targetId),
    commands: NOT_INCORTA.includes(targetId)
      ? POD_COMMANDS
      : [...POD_COMMANDS, INCORTA_RESOURCES],
  };
}

export interface CliMapContext {
  cluster: string;
  services: ClusterService[];
  byId: Map<string, GraphNode>;
}

const serviceOf = (services: ClusterService[], id: string): ServiceShape =>
  services.find(service => service.id === id) ?? { workload: id, roles: [] };

export function cliTargetOf(
  node: GraphNode,
  context: CliMapContext,
): CliMapTarget | null {
  const { cluster, services, byId } = context;
  const { detail } = node;
  switch (detail.kind) {
    case 'cluster':
      return { targetId: null, where: cluster, commands: CLUSTER_COMMANDS };
    case 'service':
      return workloadTarget(cluster, detail.service, null);
    case 'pod':
      return workloadTarget(cluster, detail.service, detail.pod.name);
    case 'infraPod':
      return workloadTarget(
        cluster,
        serviceOf(services, detail.pod.serviceId),
        detail.pod.name,
      );
    case 'volume': {
      const pod = byId.get(podNodeId(detail.pod));
      const target = pod && cliTargetOf(pod, context);
      return target?.targetId ? { ...target, commands: [DISK_SPACE] } : null;
    }
    case 'dependency':
      return detail.dependency.kind === 'bucket'
        ? {
            targetId: null,
            where: 'the cluster bucket',
            commands: BUCKET_COMMANDS,
          }
        : null;
    default:
      return null;
  }
}

const family = (targetId: string) => targetId.replace(/-\d+$/, '');

export function sphereRunsTarget(
  node: GraphNode,
  targetId: string,
  { cluster, services }: Omit<CliMapContext, 'byId'>,
) {
  const { detail } = node;
  if (detail.kind === 'pod' || detail.kind === 'infraPod') {
    const service =
      detail.kind === 'pod'
        ? detail.service
        : serviceOf(services, detail.pod.serviceId);
    return (
      workloadTarget(cluster, service, detail.pod.name)?.targetId === targetId
    );
  }
  if (detail.kind !== 'service') return false;
  const own = workloadTarget(cluster, detail.service, null)?.targetId;
  return Boolean(own) && family(own) === family(targetId);
}

export function isQuickCommandAllowed(
  command: CliQuickCommand,
  catalog: CliCommandSpec[],
) {
  const spec = catalog.find(
    each =>
      each.category === command.category && each.command === command.command,
  );
  return !spec || (spec.allowed && !spec.unsupported);
}

export function mapPathOfTarget(cluster: string, target: CliTarget) {
  const number = target.node_number ?? 0;
  if (target.kind === 'self-managed') {
    const workload = `${cluster}-${target.self_managed_name}`;
    return [serviceNodeId(workload), podNodeId(`${workload}-${number}`)];
  }
  const unnumbered = Object.keys(UNNUMBERED).find(
    workload => UNNUMBERED[workload] === target.node,
  );
  if (unnumbered) return [serviceNodeId(unnumbered)];
  const workload =
    target.node === 'analytics' ? cluster : `${cluster}-${target.node}`;
  return [serviceNodeId(workload), podNodeId(`${workload}-${number}`)];
}
